/**
 * PasswordStrengthMeter Component
 * Displays password strength visualization with detailed feedback
 *
 * Features:
 * - Real-time strength calculation
 * - Visual strength bar with color coding
 * - Detailed feedback on missing requirements
 * - Configurable strength rules
 * - Accessibility features for screen readers
 *
 * Color Scheme:
 * - WEAK (red): #ef4444 - Less than 8 chars or missing multiple requirements
 * - FAIR (yellow): #eab308 - 8+ chars, some requirements met
 * - GOOD (blue): #3b82f6 - 12+ chars, most requirements met
 * - STRONG (green): #22c55e - 16+ chars, all requirements met
 *
 * Usage:
 * ```tsx
 * <PasswordStrengthMeter
 *   password={password}
 *   rules={{ minLength: 8, requireUppercase: true }}
 *   showFeedback={true}
 * />
 * ```
 */

import React, { useMemo } from 'react';
import {
  validatePasswordStrength,
  PasswordStrength,
  PasswordStrengthRules,
} from '@atlas-forms/validation-js';
import './PasswordStrengthMeter.css';

export interface PasswordStrengthMeterProps {
  /** The password value to analyze */
  password: string;

  /** Optional password strength rules (uses defaults if not provided) */
  rules?: PasswordStrengthRules;

  /** Show detailed feedback messages (default: true) */
  showFeedback?: boolean;

  /** Show strength score percentage (default: true) */
  showScore?: boolean;

  /** Custom CSS class name */
  className?: string;

  /** Aria label for screen readers */
  ariaLabel?: string;
}

/**
 * Map strength levels to color values
 */
const STRENGTH_COLORS = {
  [PasswordStrength.WEAK]: '#ef4444', // red
  [PasswordStrength.FAIR]: '#eab308', // yellow
  [PasswordStrength.GOOD]: '#3b82f6', // blue
  [PasswordStrength.STRONG]: '#22c55e', // green
};

/**
 * Get human-readable strength text
 */
const getStrengthText = (strength: PasswordStrength): string => {
  switch (strength) {
    case PasswordStrength.WEAK:
      return 'Weak';
    case PasswordStrength.FAIR:
      return 'Fair';
    case PasswordStrength.GOOD:
      return 'Good';
    case PasswordStrength.STRONG:
      return 'Strong';
    default:
      return 'Unknown';
  }
};

/**
 * Get strength description for accessibility
 */
const getStrengthDescription = (strength: PasswordStrength, score: number): string => {
  return `Password strength: ${getStrengthText(strength)} (${score}%)`;
};

/**
 * PasswordStrengthMeter component
 * Displays visual indicator of password strength with optional feedback
 */
export const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  rules,
  showFeedback = true,
  showScore = true,
  className,
  ariaLabel,
}) => {
  // Calculate strength details
  const result = useMemo(() => {
    return validatePasswordStrength(password, rules);
  }, [password, rules]);

  // Handle empty password
  if (!password) {
    return null;
  }

  const strengthColor = STRENGTH_COLORS[result.strength];
  const strengthText = getStrengthText(result.strength);
  const strengthDescription = getStrengthDescription(result.strength, result.score);

  return (
    <div className={`af-password-strength-meter ${className || ''}`} role="status" aria-label={ariaLabel || strengthDescription}>
      {/* Strength Bar */}
      <div className="af-strength-bar-container">
        <div
          className={`af-strength-bar af-strength-${result.strength}`}
          style={{
            width: `${result.score}%`,
            backgroundColor: strengthColor,
          }}
          role="progressbar"
          aria-valuenow={result.score}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Password strength progress"
        />
      </div>

      {/* Strength Label */}
      <div className="af-strength-label">
        <span
          className={`af-strength-text af-strength-${result.strength}`}
          style={{ color: strengthColor }}
        >
          {strengthText}
        </span>

        {/* Score Display */}
        {showScore && (
          <span className="af-strength-score">
            {result.score}%
          </span>
        )}
      </div>

      {/* Feedback Messages */}
      {showFeedback && result.feedback.length > 0 && (
        <div className="af-strength-feedback" role="alert" aria-live="polite">
          <ul className="af-feedback-list">
            {result.feedback.map((message) => (
              <li key={message} className="af-feedback-item">
                <span className="af-feedback-icon">•</span>
                {message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
